import { Link } from 'react-router-dom';
import { ClipboardEdit } from 'lucide-react';
import { BoardHearingItem } from '../../types';
import { prepMeta } from '../../lib/meta';
import { Badge, Button } from '../ui';

/**
 * One hearing row on the Practice board. Title links to the case; chips carry
 * purpose + prep status; unverified auto-fetched hearings get an amber
 * "auto — verify" badge; shared cases show who shared them.
 */
export function HearingCard({
  item,
  onLogOutcome,
}: {
  item: BoardHearingItem;
  /** Opens the assistant pre-scoped to this hearing. */
  onLogOutcome?: (item: BoardHearingItem) => void;
}) {
  const prep = prepMeta(item.prep_status);
  const PrepIcon = prep.icon;
  const subtitle = [item.client_name, item.court_name, item.case_number]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="bg-white rounded-card border border-legal-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={`/app/cases/${item.matter_id}`}
            className="font-medium text-legal-900 hover:text-accent-700 transition-colors break-words"
          >
            {item.case_title}
          </Link>
          {subtitle && <p className="text-xs text-legal-400 mt-0.5 break-words">{subtitle}</p>}
        </div>
        {onLogOutcome && (
          <Button
            variant="secondary"
            size="md"
            data-track="log-outcome"
            onClick={() => onLogOutcome(item)}
            className="shrink-0 px-3"
          >
            <ClipboardEdit className="h-4 w-4" />
            <span className="hidden sm:inline">Log outcome</span>
            <span className="sm:hidden">Outcome</span>
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
        {item.purpose && <Badge tone="info">{item.purpose}</Badge>}
        <Badge className={`${prep.bg} ${prep.text}`}>
          <PrepIcon className="h-3 w-3" />
          {prep.label}
        </Badge>
        {!item.verified && (
          <Badge tone="warning" data-tour="provenance-chip">
            auto — verify
          </Badge>
        )}
        {item.shared_by && <Badge tone="accent">Shared by {item.shared_by}</Badge>}
      </div>
      {item.prep_note && (
        <p className="text-xs text-legal-500 mt-2 break-words">{item.prep_note}</p>
      )}
    </div>
  );
}
